/**
 * MarketingConsole — admin console for SEO/AEO file editing, Meta Ads
 * insights, conversion attribution and AI-driven daily briefs.
 *
 * Phase 1 (live): SEO/AEO file editor — robots.txt, sitemap.xml, llms.txt,
 *                 index.html meta tags, JSON-LD structured data
 * Phase 3+ (queued): Meta Ads insights tab, attribution tab, brief tab
 *
 * Designed to be portable — all admin auth & data fetch goes via the shared
 * `api` axios instance + `useAuth` hook so this whole page can be lifted into
 * a sibling Emergent app with only `api` and `auth` to wire up.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { blogCoverUrl } from "@/pages/LearnMore";
import { clearConfigCache } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import {
  Megaphone,
  FileText,
  Tags,
  Save,
  ArrowLeft,
  Shield,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Plus,
  Trash2,
  HelpCircle,
  Newspaper,
  Pencil,
  Upload,
  Link2,
  X,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Copy,
  Download,
  ExternalLink,
  Rocket,
} from "lucide-react";
import AiAssistant from "@/components/marketing/AiAssistant";

const TABS = [
  { key: "ai",       label: "AI Assistant",  icon: Sparkles },
  { key: "files",    label: "SEO files",     icon: FileText },
  { key: "meta",     label: "Meta tags",     icon: Tags },
  { key: "jsonld",   label: "FAQ & schema", icon: HelpCircle },
  { key: "blog",     label: "Blog",          icon: Newspaper },
  // Phase-3+ tabs reserved (rendered as "Coming next" stubs):
  { key: "meta_ads", label: "Meta Ads",      icon: Megaphone },
];

export default function MarketingConsole() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/sign-in?redirect=/admin/marketing");
  }, [loading, user, navigate]);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-12" data-testid="marketing-console">
        {/* Breadcrumb */}
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-6">
          <Link to="/admin" className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Admin Console
          </Link>
          <span aria-hidden>·</span>
          <span>Marketing</span>
        </div>
        <MarketingPanel />
      </main>
      <Footer />
    </div>
  );
}

// Chrome-less embeddable variant — used by the unified Admin Console at /admin.
// Kept exported so the same UI renders identically in both the standalone
// /admin/marketing page and inside the Admin tab shell.
export function MarketingPanel() {
  const [tab, setTab] = useState("ai");
  return (
    <div data-testid="marketing-panel">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Marketing</p>
        <h2 className="mt-2 font-display text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          SEO, AEO &amp; ad performance
        </h2>
        <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 max-w-2xl">
          Generate Meta Ads strategy and SEO/AEO content with the AI Assistant, then edit the files
          that determine how Google, Bing, ChatGPT, Perplexity, and Claude index your app.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="mt-6 flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => !t.disabled && setTab(t.key)}
              disabled={t.disabled}
              data-testid={`marketing-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                active
                  ? "border-teal-600 dark:border-teal-500 text-slate-900 dark:text-slate-50"
                  : t.disabled
                    ? "border-transparent text-slate-400 cursor-not-allowed"
                    : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
              {t.disabled && <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400">soon</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "ai"       && <AiAssistant />}
        {tab === "files"    && <SeoFilesEditor />}
        {tab === "meta"     && <MetaTagsEditor />}
        {tab === "jsonld"   && <FaqEditor />}
        {tab === "blog"     && <BlogManager />}
        {tab === "meta_ads" && <MetaAdsPlaceholder />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// SEO files (robots.txt / sitemap.xml / llms.txt)
// ----------------------------------------------------------------------
function SeoFilesEditor() {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/seo-files")
      .then(({ data }) => {
        setFiles(data.files);
        if (data.files.length) setSelected(data.files[0].name);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load SEO files"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/admin/marketing/seo-files/${selected}`)
      .then(({ data }) => setContent(data.content || ""))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load file"));
  }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/marketing/seo-files/${selected}`, { content });
      toast.success(`${selected} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="seo-files-editor">
      <aside className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 self-start">
        {files.map((f) => (
          <button
            key={f.name}
            onClick={() => setSelected(f.name)}
            data-testid={`seo-file-${f.name.replace(".", "-")}`}
            className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${
              selected === f.name
                ? "bg-teal-50 dark:bg-teal-500/10 text-teal-900 dark:text-teal-300"
                : "hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
            }`}
          >
            <p className="font-mono text-xs">{f.name}</p>
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{f.description}</p>
            <p className="mt-0.5 text-[10px] text-slate-400 font-mono">
              {f.exists ? `${f.size_bytes}B` : "not created"}
            </p>
          </button>
        ))}
      </aside>
      <div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          data-testid="seo-file-textarea"
          spellCheck="false"
          className="w-full h-[480px] px-4 py-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
        />
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Saves directly to <code className="font-mono">/app/frontend/public/{selected}</code>.
            Changes take effect on next page load (no rebuild required for static files).
          </p>
          <button
            onClick={save}
            disabled={saving}
            data-testid="seo-file-save"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : `Save ${selected}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Meta tags (index.html)
// ----------------------------------------------------------------------
function MetaTagsEditor() {
  const [tags, setTags] = useState({});
  const [schema, setSchema] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/meta-tags")
      .then(({ data }) => {
        setTags(data.tags || {});
        setSchema(data.schema || {});
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load meta tags"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send tags that actually have a value — unset og/twitter tags come
      // back as null from GET, and null fails backend validation (422).
      const cleanTags = {};
      for (const [k, v] of Object.entries(tags)) {
        if (v !== null && v !== undefined) cleanTags[k] = v;
      }
      const { data } = await api.put("/admin/marketing/meta-tags", { tags: cleanTags });
      toast.success(`${data.changed.length} tag(s) updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div data-testid="meta-tags-editor">
      <div className="rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-900 dark:text-amber-200">
          Meta tags drive the social-share preview (Facebook, LinkedIn, Twitter/X, WhatsApp)
          and the snippet under your Google result. Keep titles under 60 characters and
          descriptions under 155 to avoid truncation.
          <span className="block mt-1.5 font-medium">Saved changes are applied live at runtime — no redeploy needed (they load on the next page view).</span>
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {Object.entries(schema).map(([key, cfg]) => {
          const isTextarea = key === "description" || key.endsWith("description");
          return (
            <div key={key} className={isTextarea ? "md:col-span-2" : ""}>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">
                {cfg.label}
                <span className="ml-2 font-mono text-[10px] text-slate-400 normal-case">
                  {cfg.kind === "title" ? "<title>" : cfg.selector}
                </span>
              </label>
              {isTextarea ? (
                <textarea
                  value={tags[key] ?? ""}
                  onChange={(e) => setTags({ ...tags, [key]: e.target.value })}
                  data-testid={`meta-tag-${key}`}
                  className="w-full h-24 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
                />
              ) : (
                <input
                  value={tags[key] ?? ""}
                  onChange={(e) => setTags({ ...tags, [key]: e.target.value })}
                  data-testid={`meta-tag-${key}`}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
                />
              )}
              {tags[key] && (
                <p className="mt-1 text-[10px] text-slate-400 font-mono">
                  {tags[key].length} chars
                </p>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex items-center justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="meta-tags-save"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save meta tags
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// JSON-LD structured data (between SEO:JSONLD markers in index.html)
// ----------------------------------------------------------------------
function FaqEditor() {
  const [items, setItems] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/marketing/faqs")
      .then(({ data }) => { setItems(data.items || []); setIsCustom(!!data.is_custom); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load FAQs"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const update = (i, key, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { question: "", answer: "" }]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const save = async () => {
    const clean = items
      .map((it) => ({ question: (it.question || "").trim(), answer: (it.answer || "").trim() }))
      .filter((it) => it.question && it.answer);
    if (!clean.length) { toast.error("Add at least one complete question and answer."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marketing/faqs", { items: clean });
      setItems(data.items || clean);
      setIsCustom(true);
      clearConfigCache();
      toast.success(`Saved ${data.count} FAQ${data.count === 1 ? "" : "s"} — live on /faq, Pricing & search schema.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div data-testid="faq-editor">
      <div className="rounded-lg border border-teal-300 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-4 mb-5 flex items-start gap-3">
        <HelpCircle className="h-4 w-4 text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" />
        <p className="text-xs text-teal-900 dark:text-teal-200 leading-relaxed">
          These question &amp; answer pairs are the <b>single source</b> for your public FAQ (on <b>/faq</b> and the <b>Plans &amp; Pricing</b> page)
          <b> and</b> the FAQPage structured data that Google &ldquo;People also ask&rdquo; and AI overviews read — generated automatically, so there&rsquo;s no JSON to hand-edit.
          {isCustom ? "" : " You're currently showing the default FAQs; edit and save to customise."}
        </p>
      </div>

      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} data-testid={`faq-editor-item-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Q{i + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`faq-up-${i}`} className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30" title="Move up">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`faq-down-${i}`} className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30" title="Move down">↓</button>
                <button onClick={() => remove(i)} data-testid={`faq-remove-${i}`} className="p-1 text-rose-400 hover:text-rose-600" title="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <input
              value={it.question}
              onChange={(e) => update(i, "question", e.target.value)}
              data-testid={`faq-question-input-${i}`}
              placeholder="Question"
              className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
            />
            <textarea
              value={it.answer}
              onChange={(e) => update(i, "answer", e.target.value)}
              data-testid={`faq-answer-input-${i}`}
              placeholder="Answer"
              rows={3}
              className="mt-2 w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
            />
          </div>
        ))}
      </div>

      <button
        onClick={add}
        data-testid="faq-add"
        className="mt-4 inline-flex items-center gap-2 text-sm text-teal-700 dark:text-teal-400 hover:underline underline-offset-2"
      >
        <Plus className="h-4 w-4" /> Add a question
      </button>

      <div className="mt-6 flex items-center justify-end border-t border-slate-200 dark:border-slate-800 pt-4">
        <button
          onClick={save}
          disabled={saving}
          data-testid="faq-save"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save FAQs
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Meta Ads dashboard — mock data until credentials are saved via the
// in-app "Connect Meta" form. NO terminal / NO .env editing required.
// ----------------------------------------------------------------------
function MetaAdsPlaceholder() {
  return <MetaAdsDashboard />;
}

function MetaAdsDashboard() {
  const [status, setStatus] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        api.get("/admin/marketing/meta-ads/status"),
        api.get("/admin/marketing/meta-ads/insights"),
      ]);
      setStatus(s.data);
      setInsights(i.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Meta Ads data");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/marketing/meta-ads/test");
      if (data.ok) {
        toast.success(`Connected to "${data.ad_account_name || "ad account"}"${data.currency ? ` · ${data.currency}` : ""}. Scopes: ${(data.scopes || []).join(", ")}`);
      } else {
        toast.error(data.error || "Connection test failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading || !insights) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const isMock = insights.source === "mock";
  const s = insights.summary || {};

  return (
    <div data-testid="meta-ads-dashboard" className="space-y-6">
      {/* Mode banner */}
      <div className={`rounded-lg border p-4 flex items-start justify-between gap-3 flex-wrap ${
        isMock
          ? "border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10"
          : "border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10"
      }`}>
        <div className="flex items-start gap-3 flex-1 min-w-[240px]">
          {isMock
            ? <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            : <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />}
          <div>
            <p className={`text-sm font-semibold ${isMock ? "text-amber-900 dark:text-amber-200" : "text-emerald-900 dark:text-emerald-200"}`}>
              {isMock ? "Sample data shown — Meta Ads is not yet connected" : "Live Meta Ads data"}
            </p>
            <p className={`mt-1 text-xs ${isMock ? "text-amber-900/80 dark:text-amber-200/80" : "text-emerald-900/80 dark:text-emerald-200/80"}`}>
              {isMock
                ? "Click &quot;Connect Meta&quot; to paste your 4 Meta credentials. The dashboard structure is real — only the numbers below are made up."
                : "Refreshed from Meta's Marketing API. Last-7-days insights."}
            </p>
            {insights.live_error && (
              <p className="mt-1 text-xs font-mono text-rose-700 dark:text-rose-400 break-words">
                {insights.live_error}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status?.configured && (
            <button
              onClick={testConnection}
              disabled={testing}
              data-testid="meta-test-button"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Test connection
            </button>
          )}
          <button
            onClick={() => setShowConnect(true)}
            data-testid="meta-connect-button"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
          >
            <Megaphone className="h-4 w-4" />
            {status?.configured ? "Update Meta credentials" : "Connect Meta"}
          </button>
        </div>
      </div>

      {/* Ad Launchpad — prepare images + approved copy, then hand off to Ads Manager */}
      <AdLaunchpad adAccountId={status?.ad_account_id} />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile label="Spend (7d)" value={`$${(s.spend_usd ?? 0).toFixed(2)}`} hint="USD" />
        <KpiTile label="CTR" value={`${(s.ctr_pct ?? 0).toFixed(2)}%`} hint={`${s.clicks ?? 0} clicks · ${s.impressions ?? 0} impressions`} />
        <KpiTile label="Conversions" value={s.conversions ?? 0} hint={`$${(s.cost_per_conversion_usd ?? 0).toFixed(2)} cost / conv.`} />
        <KpiTile label="Weighted ROAS" value={`${(s.weighted_roas ?? 0).toFixed(2)}×`} hint="$27=1× · $125=5× · scan=0.1×" tone={s.weighted_roas >= 1 ? "good" : "bad"} />
      </div>

      {/* Campaigns table */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Campaigns</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-950/60">
              <tr className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2.5 font-medium">Campaign</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium text-right">Spend</th>
                <th className="px-3 py-2.5 font-medium text-right">CTR</th>
                <th className="px-3 py-2.5 font-medium text-right">Conv.</th>
                <th className="px-3 py-2.5 font-medium text-right">Cost / conv.</th>
              </tr>
            </thead>
            <tbody>
              {(insights.campaigns || []).map((c, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                  <td className="px-3 py-2.5 text-slate-800 dark:text-slate-100">{c.name}</td>
                  <td className="px-3 py-2.5"><StatusPill status={c.status} /></td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">${c.spend_usd.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{c.ctr_pct.toFixed(2)}%</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{c.conversions}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{c.cpr_usd != null ? `$${c.cpr_usd.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top ads */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Top-performing ad copy</p>
        <ul className="mt-3 space-y-3">
          {(insights.top_ads || []).map((a, i) => (
            <li key={i} className="flex items-start justify-between gap-4">
              <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">{a.headline}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-mono whitespace-nowrap">
                {a.ctr_pct.toFixed(2)}% · {a.conversions} conv.
              </p>
            </li>
          ))}
        </ul>
      </div>

      {showConnect && <MetaConnectModal onClose={() => { setShowConnect(false); reload(); }} status={status} />}
    </div>
  );
}

function KpiTile({ label, value, hint, tone }) {
  const v = tone === "good" ? "text-emerald-700 dark:text-emerald-400"
          : tone === "bad"  ? "text-rose-700 dark:text-rose-400"
                            : "text-slate-900 dark:text-slate-50";
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${v}`}>{value}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "ACTIVE"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>{status}</span>;
}

// ----------------------------------------------------------------------
// AdLaunchpad — assemble uploaded images + approved ad copy (reused from the
// AI Assistant) + audience interests into a ready pack, then deep-link into
// Ads Manager to publish. No ads_management / App Review required.
// ----------------------------------------------------------------------
function AdLaunchpad({ adAccountId }) {
  const [strategy, setStrategy] = useState(null);
  const [assets, setAssets] = useState([]);
  const [copyIdx, setCopyIdx] = useState(0);
  const [picked, setPicked] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, a] = await Promise.all([
        api.get("/admin/marketing/ai/history", { params: { type: "strategy", limit: 1 } }),
        api.get("/admin/marketing/ad-assets"),
      ]);
      setStrategy(h.data.recommendations?.[0]?.output || null);
      setAssets(a.data.assets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load launchpad");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/marketing/ad-assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAssets((prev) => [data.asset, ...prev]);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAsset = async (id) => {
    try {
      await api.delete(`/admin/marketing/ad-assets/${id}`);
      setAssets((prev) => prev.filter((x) => x.id !== id));
      setPicked((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const togglePick = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const copy = (label, text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Copy failed"));
  };

  const base = process.env.REACT_APP_BACKEND_URL || "";
  const acctNum = (adAccountId || "").replace(/^act_/, "");
  const adsManagerUrl = acctNum
    ? `https://www.facebook.com/adsmanager/manage/campaigns/new?act=${acctNum}`
    : "https://www.facebook.com/adsmanager";

  const adCopy = strategy?.ad_copy || [];
  const chosen = adCopy[copyIdx] || null;
  const interests = strategy?.keywords_interests || [];

  if (!open) {
    return (
      <div className="rounded-lg border border-teal-300 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Rocket className="h-5 w-5 text-teal-700 dark:text-teal-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">Ad Launchpad</p>
            <p className="mt-1 text-xs text-teal-900/80 dark:text-teal-100/80 max-w-xl">
              Assemble your images + approved ad copy + audience into a ready pack, then hop into Ads Manager to publish — minimal time in Meta&rsquo;s interface.
            </p>
          </div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="open-launchpad" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors">
          <Rocket className="h-4 w-4" /> Open Launchpad
        </button>
      </div>
    );
  }

  return (
    <div data-testid="ad-launchpad" className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Ad Launchpad</h3>
        </div>
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-sm">Close</button>
      </div>

      {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <section>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">1 · Ad images</p>
            <div className="mt-3 flex flex-wrap gap-3">
              {assets.map((a) => (
                <div key={a.id} className={`relative rounded-lg overflow-hidden border-2 ${picked.includes(a.id) ? "border-teal-500" : "border-slate-200 dark:border-slate-800"}`}>
                  <img src={`${base}${a.url}`} alt={a.filename || "ad"} className="h-24 w-24 object-cover cursor-pointer" onClick={() => togglePick(a.id)} data-testid={`asset-${a.id}`} />
                  <button onClick={() => removeAsset(a.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5" aria-label="Delete image"><X className="h-3 w-3" /></button>
                  {picked.includes(a.id) && <span className="absolute bottom-1 left-1 bg-teal-600 text-white text-[9px] px-1.5 py-0.5 rounded">selected</span>}
                </div>
              ))}
              <label className="h-24 w-24 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:border-teal-500 hover:text-teal-600 transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" /><span className="mt-1 text-[10px]">Upload</span></>}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="launchpad-upload" />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Click an image to select it for the pack. PNG/JPG up to 8MB. Recommended 1080×1080 or 1200×628.</p>
          </section>

          <section>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">2 · Ad copy <span className="normal-case tracking-normal text-slate-400">(from your approved AI Assistant variations)</span></p>
            {adCopy.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No ad copy yet — generate &amp; approve it in the AI Assistant tab first.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {adCopy.map((a, i) => (
                  <label key={i} className={`block rounded-lg border p-3 cursor-pointer ${copyIdx === i ? "border-teal-500 bg-teal-50/50 dark:bg-teal-500/5" : "border-slate-200 dark:border-slate-800"}`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" name="adcopy" checked={copyIdx === i} onChange={() => setCopyIdx(i)} className="mt-1" data-testid={`launch-copy-${i}`} />
                      <div className="text-sm">
                        <p className="font-medium text-slate-900 dark:text-slate-50">{a.headline}</p>
                        <p className="text-slate-600 dark:text-slate-300">{a.primary_text}</p>
                        <p className="text-[12px] text-slate-500 dark:text-slate-400">{a.description} · <span className="font-mono">{a.cta}</span></p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 bg-slate-50/60 dark:bg-slate-950/40">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">3 · Launch pack — copy into Ads Manager</p>
            {chosen && (
              <div className="mt-3 space-y-2">
                {[["Headline", chosen.headline], ["Primary text", chosen.primary_text], ["Description", chosen.description], ["Call to action", chosen.cta]].map(([label, val]) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-[11px] w-28 shrink-0 text-slate-500 dark:text-slate-400">{label}</span>
                    <span className="flex-1 text-sm text-slate-800 dark:text-slate-100 truncate">{val || "—"}</span>
                    <button onClick={() => copy(label, val)} data-testid={`copy-${label}`} className="inline-flex items-center gap-1 text-xs text-teal-700 dark:text-teal-400 hover:underline"><Copy className="h-3.5 w-3.5" /> Copy</button>
                  </div>
                ))}
                {interests.length > 0 && (
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                    <span className="text-[11px] w-28 shrink-0 text-slate-500 dark:text-slate-400">Interests</span>
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{interests.join(", ")}</span>
                    <button onClick={() => copy("Interests", interests.join(", "))} className="inline-flex items-center gap-1 text-xs text-teal-700 dark:text-teal-400 hover:underline"><Copy className="h-3.5 w-3.5" /> Copy</button>
                  </div>
                )}
              </div>
            )}
            {picked.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {assets.filter((a) => picked.includes(a.id)).map((a) => (
                  <a key={a.id} href={`${base}${a.url}`} download={a.filename || "ad-image"} className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"><Download className="h-3.5 w-3.5" /> {a.filename || "image"}</a>
                ))}
              </div>
            )}
            <a href={adsManagerUrl} target="_blank" rel="noopener noreferrer" data-testid="open-ads-manager" className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors">
              <ExternalLink className="h-4 w-4" /> Open Ads Manager to publish
            </a>
            <ol className="mt-3 space-y-1 list-decimal list-inside text-[12px] text-slate-500 dark:text-slate-400">
              <li>In Ads Manager, create a campaign (objective: Sales or Leads).</li>
              <li>Set the audience — paste the interests above under Detailed Targeting.</li>
              <li>Upload the downloaded image(s) as the creative.</li>
              <li>Paste the headline, primary text, description and CTA; set the link to your scan URL.</li>
              <li>Review and Publish.</li>
            </ol>
          </section>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// MetaConnectModal — in-app credentials manager. Writes to the DB via
// PUT /admin/marketing/meta-ads/credentials. Admin never touches .env.
// ----------------------------------------------------------------------
function MetaConnectModal({ onClose, status }) {
  const fields = [
    {
      key: "META_APP_ID",
      label: "App ID",
      placeholder: "1234567890123456",
      help: "Found on developers.facebook.com → your App → top of the page (under the app name).",
      finder: "https://developers.facebook.com/apps",
    },
    {
      key: "META_APP_SECRET",
      label: "App Secret",
      placeholder: "abc123…",
      help: "developers.facebook.com → your App → Settings → Basic → 'App Secret' → click 'Show'.",
      finder: "https://developers.facebook.com/apps",
    },
    {
      key: "META_ACCESS_TOKEN",
      label: "System User Access Token",
      placeholder: "EAA…very long token",
      help: "business.facebook.com → Business Settings → System Users → 'marketing-reader-bot' → Generate New Token. Pick scope 'ads_read' only.",
      finder: "https://business.facebook.com/settings/system-users",
    },
    {
      key: "META_AD_ACCOUNT_ID",
      label: "Ad Account ID",
      placeholder: "act_123456789",
      help: "business.facebook.com → Business Settings → Ad Accounts → pick the one you advertise from. Must include the act_ prefix.",
      finder: "https://business.facebook.com/settings/ad-accounts",
    },
    {
      key: "META_PIXEL_ID",
      label: "Meta Pixel ID",
      placeholder: "1070333925387015",
      help: "Events Manager → Data sources → your Pixel → Settings → 'Dataset ID'. Powers on-site conversion tracking (PageView, Lead, Purchase). This is a PUBLIC value.",
      finder: "https://www.facebook.com/events_manager2",
    },
  ];
  const savedHints = { ...(status?.credentials_masked || {}), META_PIXEL_ID: status?.meta_pixel_id || null };
  const [values, setValues] = useState({ META_PIXEL_ID: status?.meta_pixel_id || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        if (values[f.key] !== undefined && values[f.key] !== "") {
          payload[f.key] = values[f.key];
        }
      }
      if (Object.keys(payload).length === 0) { onClose(); return; }
      const { data } = await api.put("/admin/marketing/meta-ads/credentials", payload);
      toast.success(data.configured ? "Meta Ads is now connected." : "Saved — fill remaining fields to go live.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="meta-connect-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-teal-700 dark:text-teal-400">Meta Ads</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-slate-900 dark:text-slate-50">Connect your Meta account</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-4 text-sm text-teal-900 dark:text-teal-100" data-testid="meta-getting-started">
            <p className="font-semibold">Getting started — one-time setup (~10 min)</p>
            <ol className="mt-2 space-y-1.5 list-decimal list-inside text-teal-900/90 dark:text-teal-100/90">
              <li>Create or open a Meta app at <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a> → <span className="font-medium">My Apps</span> → choose the <span className="font-medium">Business</span> type.</li>
              <li>In <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" className="underline">Business Settings → Ad Accounts</a>, confirm the ad account you advertise from is listed.</li>
              <li>Open <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer" className="underline">Business Settings → System Users</a> → <span className="font-medium">Add</span> → name it <code className="font-mono text-xs">marketing-reader-bot</code>.</li>
              <li>Assign that system user to your ad account with <span className="font-medium">View performance</span> access.</li>
              <li>On the system user click <span className="font-medium">Generate New Token</span> → select your app → tick <code className="font-mono text-xs">ads_read</code> only → copy the long token.</li>
              <li>Copy your <span className="font-medium">App ID</span> + <span className="font-medium">App Secret</span> (App → Settings → Basic) and <span className="font-medium">Ad Account ID</span> (starts with <code className="font-mono text-xs">act_</code>).</li>
              <li>Grab your <span className="font-medium">Pixel ID</span> from <a href="https://www.facebook.com/events_manager2" target="_blank" rel="noopener noreferrer" className="underline">Events Manager</a> → your Pixel → Settings (this powers on-site conversion tracking).</li>
              <li>Paste them all below → <span className="font-medium">Save</span>, then <span className="font-medium">Test connection</span>.</li>
            </ol>
          </div>
          <div className="rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
            <p className="font-semibold">Before you paste these in</p>
            <p className="mt-1.5">These values are sent securely to your app and stored for Meta Ads reporting. Treat the <strong>App Secret</strong> and <strong>System User Access Token</strong> as sensitive credentials. Only add tokens with the minimum permissions required (<code className="font-mono text-xs">ads_read</code> is enough for reporting).</p>
            <p className="mt-1.5 text-xs text-amber-900/80 dark:text-amber-200/80">The links beside each field open the exact Meta page where you&rsquo;ll find that value.</p>
          </div>

          {fields.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  {f.label}
                  {savedHints[f.key] && (
                    <span className="ml-2 font-mono text-[10px] text-emerald-700 dark:text-emerald-400 normal-case">
                      saved: {savedHints[f.key]}
                    </span>
                  )}
                </label>
                <a href={f.finder} target="_blank" rel="noopener noreferrer" className="text-[11px] text-teal-700 dark:text-teal-400 hover:underline">
                  open Meta →
                </a>
              </div>
              <input
                type={f.key === "META_APP_SECRET" || f.key === "META_ACCESS_TOKEN" ? "password" : "text"}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                placeholder={savedHints[f.key] ? "(unchanged — leave blank or paste new value)" : f.placeholder}
                data-testid={`meta-cred-${f.key}`}
                className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
              />
              <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">{f.help}</p>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Leave a field blank to keep the existing value.</p>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="meta-cred-save" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save credentials
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------
// Blog manager — CRUD for Learn More articles (markdown), cover upload,
// active toggle, and best-effort import from a LinkedIn / Substack URL.
// ----------------------------------------------------------------------
const EMPTY_BLOG = {
  title: "", excerpt: "", content: "", cover_path: null,
  cover_external_url: null, source: "manual", source_url: null, active: true,
};

function BlogManager() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view; object = edit form

  const load = () => {
    setLoading(true);
    api.get("/admin/blogs")
      .then(({ data }) => setArticles(data.articles || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load articles"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/blogs/${a.id}`, { ...a, active: !a.active });
      clearConfigCache();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blogs/${a.id}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  if (editing) {
    return (
      <BlogEditor
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="blog-manager">
      <div className="rounded-lg border border-teal-300 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-4 mb-5 flex items-start gap-3">
        <Newspaper className="h-4 w-4 text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" />
        <p className="text-xs text-teal-900 dark:text-teal-200 leading-relaxed">
          Articles published here appear on the public <b>Learn more</b> page. Write in <b>Markdown</b>,
          add an optional cover image, or import a draft from a LinkedIn / Substack URL. Toggle an
          article off to hide it from visitors without deleting it.
        </p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {articles.length} article{articles.length === 1 ? "" : "s"}
        </p>
        <button
          onClick={() => setEditing({ ...EMPTY_BLOG })}
          data-testid="blog-new-btn"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
        >
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-950/60">
            <tr className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              <th className="px-4 py-2.5 font-medium">Article</th>
              <th className="px-4 py-2.5 font-medium">Source</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No articles yet — create your first one.</td></tr>
            )}
            {articles.map((a) => {
              const cover = blogCoverUrl(a);
              return (
                <tr key={a.id} data-testid={`blog-row-${a.slug}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-16 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center">
                        {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4 text-slate-300 dark:text-slate-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-50 truncate max-w-[320px]">{a.title}</p>
                        <p className="text-[11px] text-slate-400 font-mono truncate max-w-[320px]">/learn-more/{a.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 capitalize">{a.source || "manual"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${a.active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                      {a.active ? "Published" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleActive(a)} data-testid={`blog-toggle-${a.slug}`} title={a.active ? "Hide" : "Publish"} className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                        {a.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button onClick={() => setEditing(a)} data-testid={`blog-edit-${a.slug}`} title="Edit" className="p-2 text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(a)} data-testid={`blog-delete-${a.slug}`} title="Delete" className="p-2 text-rose-400 hover:text-rose-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlogEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cover = blogCoverUrl(form);

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blogs/upload-cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_path: data.cover_path, cover_external_url: null }));
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const doImport = async () => {
    if (!importUrl.trim()) { toast.error("Paste an article URL first."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/blogs/import", { url: importUrl.trim() });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        content: data.content || f.content,
        cover_path: data.cover_path || f.cover_path,
        cover_external_url: data.cover_external_url || (data.cover_path ? null : f.cover_external_url),
        source: data.source || f.source,
        source_url: data.source_url || f.source_url,
      }));
      toast.success(data.note || "Imported as a draft — review before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt || "",
        content: form.content || "",
        cover_path: form.cover_path || null,
        cover_external_url: form.cover_external_url || null,
        source: form.source || "manual",
        source_url: form.source_url || null,
        active: !!form.active,
      };
      if (isNew) await api.post("/admin/blogs", payload);
      else await api.put(`/admin/blogs/${form.id}`, payload);
      clearConfigCache();
      toast.success(isNew ? "Article created" : "Article saved — live on Learn more.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="blog-editor">
      <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to articles
      </button>

      {/* Import from URL */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4 mb-6">
        <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> Import from LinkedIn / Substack
        </p>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://…/your-article"
            data-testid="blog-import-url"
            className="flex-1 h-10 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
          />
          <button
            onClick={doImport}
            disabled={importing}
            data-testid="blog-import-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import draft
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">Pulls in the title, summary, cover, and available text. LinkedIn often needs login — you may need to paste the body manually.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">Title</label>
            <input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid="blog-field-title"
              className="w-full h-11 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-base font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">Excerpt (short summary)</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              rows={2}
              data-testid="blog-field-excerpt"
              className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">Content (Markdown)</label>
            <textarea
              value={form.content}
              onChange={(e) => set("content", e.target.value)}
              rows={20}
              spellCheck="false"
              data-testid="blog-field-content"
              placeholder="## A heading&#10;&#10;Write your article in Markdown…"
              className="w-full px-4 py-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
            />
            <p className="mt-1 text-[11px] text-slate-400">Use ## and ### for headings, - for bullet lists, **bold**, and [text](url) for links.</p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">Cover image</label>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden bg-slate-100 dark:bg-slate-800 aspect-[16/9] flex items-center justify-center">
              {cover ? <img src={cover} alt="" className="h-full w-full object-cover" data-testid="blog-cover-preview" /> : <ImageIcon className="h-6 w-6 text-slate-300 dark:text-slate-600" />}
            </div>
            <label className="mt-2 inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors w-full justify-center">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload image"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" data-testid="blog-cover-upload" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
            </label>
            {cover && (
              <button onClick={() => setForm((f) => ({ ...f, cover_path: null, cover_external_url: null }))} className="mt-2 inline-flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-700">
                <X className="h-3.5 w-3.5" /> Remove cover
              </button>
            )}
            <div className="mt-3">
              <label className="block text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">or paste image URL</label>
              <input
                value={form.cover_external_url || ""}
                onChange={(e) => setForm((f) => ({ ...f, cover_external_url: e.target.value || null, cover_path: e.target.value ? null : f.cover_path }))}
                placeholder="https://…/image.jpg"
                data-testid="blog-cover-url"
                className="w-full h-9 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
              />
            </div>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} data-testid="blog-field-active" className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
            <span className="text-sm text-slate-700 dark:text-slate-200">Published (visible on Learn more)</span>
          </label>

          <button
            onClick={save}
            disabled={saving}
            data-testid="blog-save-btn"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors w-full justify-center"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? "Create article" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
